import * as s from "../SystemSpecStyled";
import { useQuery } from "react-query";
import { getVmaxStatus } from "api/reportAPI";
import { useLocation } from "react-router-dom";

import Spinner from "components/atoms/loader/Spinner";
import { Content } from "pages/dashboard/map/sub/leftAside/detail/RecorderDetailStyled";
import VMAX from "./VMAX";
import Storage from "../../Storage";
import { useEffect, useMemo, useState } from "react";
import {
  CameraSystemStatus,
  ReportHealthDetailRecorderAlert,
  VmaxSystemStatus,
} from "api/interfaces/reportInterface.interface";
import NoData from "components/templates/nopage/NoData";
import useStompDebouncedRefetch, {
  StompDateSignalKey,
} from "hook/useStompDebouncedRefetch";

type Props = {
  recorderId: string;
  brand?: string;
  isSystemNotResponding?: ReportHealthDetailRecorderAlert | null;
};

export default function SystemVMAXSpec(props: Props): JSX.Element {
  const location = useLocation();
  const isMap: boolean = location.pathname.includes("map");
  const [isFirstLoaded, setIsFirstLoaded] = useState<boolean>(false);

  const { data, status, isLoading, error, refetch } = useQuery(
    ["vmaxStatusQuery", props.recorderId],
    () =>
      getVmaxStatus({
        recorderId: props.recorderId,
      }),
    {
      retry: 0,
      enabled: Boolean(props.recorderId),
      staleTime: 30000, // 30초
      cacheTime: 60000, // 1분
      refetchInterval: 30000, // 30초마다 refetch
      refetchOnWindowFocus: false,
      keepPreviousData: true, // 최초 성공 후 refetch 중 에러 발생해도 이전 data 유지됨
      refetchOnMount: "always",
    }
  );
  useStompDebouncedRefetch({
    stompKey: ["reportDetailUpdated"] as StompDateSignalKey[],
    refetch: refetch,
  });

  // 최초 성공 시점 기록
  useEffect(() => {
    if (status === "success" || !isLoading) {
      setIsFirstLoaded(true);
    }
  }, [status, isLoading]);
  const vmaxStatus: VmaxSystemStatus | null = useMemo(() => {
    if (!data?.result) {
      return null;
    }
    return {
      ...data?.result,
      cameras: (data?.result?.cameras || []).sort(
        (a: CameraSystemStatus, b: CameraSystemStatus) => a.channel - b.channel
      ),
    };
  }, [data]);

  /*
    렌더링 조건 요약
    ---------------
    1. 초기 로딩 중          -> data 없음  -> <Spinner />
    2. 초기 요청 에러        -> data 없음  -> <No Data />
    3. 요청 성공             -> data 있음  -> <Content />
    4. 이후 refetch 에러 발생 -> data 유지 -> <Content /> (에러 무시)
*/

  if (!isFirstLoaded) {
    return <Spinner />;
  }
  if (!vmaxStatus && (props.isSystemNotResponding || error)) {
    return <NoData />;
  }

  return (
    <Content isNoMap={!isMap}>
      <s.VmaxInner isMap={isMap}>
        {vmaxStatus && <VMAX vmaxStatus={vmaxStatus} brand={props.brand} />}
      </s.VmaxInner>
      {isMap && <Storage recorderId={props.recorderId} />}
    </Content>
  );
}
